import { useEffect, useRef, useState } from "react";
import { List, Stack, Typography } from "@mui/joy";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { SentimentType, ThoughtRecordType } from "api/thoughtRecordApi";
import ThoughtRecordListItem from "./ThoughtRecordListItem";
import AddNewThoughtRecordButton from "./AddNewThoughtRecordButton";
import TabButtonList from "../../common/TabButtonList";
import { taskListAtom } from "recoil/taskListAtom";
import { theme } from "styles/theme";
import useIsInappWebview from "hooks/useIsInappWebview";

export default function ThoughtRecordList({
  thoughtRecordList,
  currentThoughtRecord,
  setCurrentThoughtRecord,
}: {
  thoughtRecordList?: ThoughtRecordType[];
  currentThoughtRecord?: ThoughtRecordType;
  setCurrentThoughtRecord: React.Dispatch<
    React.SetStateAction<ThoughtRecordType | undefined>
  >;
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";
  const isTaskContainerSmall = useIsInappWebview();
  const [listData, setListData] = useState<ThoughtRecordType[] | undefined>(
    thoughtRecordList
  );

  const listRef = useRef<HTMLUListElement | null>(null);

  const [selectedTab, setSelectedTab] = useState<SentimentType>("negative");
  useEffect(() => {
    if (currentThoughtRecord?.sentiment) {
      setSelectedTab(currentThoughtRecord.sentiment);
    }
  }, [currentThoughtRecord]);

  const [currentTabListData, setCurrentTabListData] = useState<
    ThoughtRecordType[] | undefined
  >(thoughtRecordList?.filter((element) => element.sentiment === selectedTab));

  const taskListState = useRecoilValue(taskListAtom);
  const isPositiveAvailable = taskListState.some(
    (element) => ["7-1-A"].includes(element.taskId) && element.lastDoneAt
  );
  useEffect(() => {
    setListData(thoughtRecordList);
  }, [thoughtRecordList]);

  useEffect(() => {
    setCurrentTabListData(
      listData?.filter(
        (element) => !element.sentiment || element.sentiment === selectedTab
      )
    );
  }, [selectedTab, listData]);

  useEffect(() => {
    if (!listRef.current) return;
    if (!currentThoughtRecord || !currentTabListData || currentTabListData.length === 0) return;

    const index = currentTabListData.findIndex(
      (tr) => tr.thoughtRecordKey === currentThoughtRecord.thoughtRecordKey
    );
    if (index < 0) return;

    const container = listRef.current;
    const children = container.children;
    if (index >= children.length) return;
    const target = children[index] as HTMLElement | undefined;
    if (!target) return;

    requestAnimationFrame(() => {
      try {
        target.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
      } catch (e) {
        // Fallback manual scroll calculation
        if (isTaskContainerSmall) {
          const center = target.offsetLeft - container.clientWidth / 2 + target.clientWidth / 2;
          container.scrollTo({ left: Math.max(0, center), behavior: "smooth" as ScrollBehavior });
        } else {
          const center = target.offsetTop - container.clientHeight / 2 + target.clientHeight / 2;
          container.scrollTo({ top: Math.max(0, center), behavior: "smooth" as ScrollBehavior });
        }
      }
    });
  }, [currentThoughtRecord?.thoughtRecordKey, currentTabListData, isTaskContainerSmall, selectedTab]);

  return (
    <Stack
      alignItems="center"
      sx={{
        backgroundColor: theme.vars.palette.Background.Level1,
        flexShrink: 0,
        width: isTaskContainerSmall ? "100%" : "320px",
        height: isTaskContainerSmall ? "fit-content" : "100%",
        // overflow: "scroll",
        transition: "transform 0.4s, width 0.4s",
        scrollPaddingBlockStart: "20px",
        boxSizing: "border-box",
        ...(isTaskContainerSmall && { pb: "20px" }),
      }}
      className="scrollbar-hide"
    >
      <Stack
        direction={isTaskContainerSmall ? "row" : "column"}
        justifyContent={"center"}
        spacing={isTaskContainerSmall ? "8px" : "12px"}
        sx={{
          width: "100%",

          ...(isTaskContainerSmall
            ? {
                px: "20px",
                height: "36px",
                mb: "16px",
              }
            : {
                flexShrink: 0,
                zIndex: 10,
                position: "sticky",
                py: "8px",
                top: 0,
                px: "20px",
              }),
        }}
      >
        <Stack
          spacing={1}
          direction={isTaskContainerSmall ? "row-reverse" : "column"}
          sx={{
            flexShrink: 0,
            width: "100%",
            ...(isTaskContainerSmall && {
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }),
          }}
        >
          <TabButtonList
            selectedTab={selectedTab}
            data={[
              {
                id: "negative",
                label: "부정",
                onClick: () => {
                  setSelectedTab("negative");
                  if (currentThoughtRecord?.sentiment !== "negative") {
                    const targetList = (listData || []).filter(
                      (element) => !element.sentiment || element.sentiment === "negative"
                    );
                    if (targetList.length > 0) {
                      setCurrentThoughtRecord(targetList[0]);
                    }
                  }
                },
              },
              {
                id: "positive",
                label: "긍정",
                onClick: () => {
                  setSelectedTab("positive");
                  if (currentThoughtRecord?.sentiment !== "positive") {
                    const targetList = (listData || []).filter(
                      (element) => !element.sentiment || element.sentiment === "positive"
                    );
                    if (targetList.length > 0) {
                      setCurrentThoughtRecord(targetList[0]);
                    }
                  }
                },
              },
            ]}
          />
          <AddNewThoughtRecordButton
            disabled={
              isCoach ||
              (selectedTab === "positive" && !isPositiveAvailable) ||
              ((listData || []).length === 1 &&
                !listData?.every((element) => element.lastDoneAt))
            }
            sentiment={selectedTab}
          />
        </Stack>
      </Stack>

      <List
        ref={listRef}
        orientation={isTaskContainerSmall ? "horizontal" : "vertical"}
        className="scrollbar-hide"
        sx={{
          py: 0,
          px: "20px",
          overflow: "scroll",
          width: "100%",
          "--ListItem-paddingY": 0,
          "--ListItem-paddingX": 0,
          ...(isTaskContainerSmall && { gap: "4px" }),
        }}
      >
        {currentTabListData && currentTabListData.length > 0 ? (
          currentTabListData.map((thoughtRecord, index) => {
            return (
              <ThoughtRecordListItem
                key={thoughtRecord.thoughtRecordKey}
                width={isTaskContainerSmall ? "58px" : "100%"}
                direction={isTaskContainerSmall ? "row" : "column"}
                isFirstRecordOfMonth={
                  index === 0 ||
                  (index - 1 >= 0 &&
                    dayjs(currentTabListData[index - 1].date).get("month") !==
                      dayjs(currentTabListData[index].date).get("month"))
                }
                thoughtRecord={thoughtRecord}
                isSelected={
                  currentThoughtRecord?.thoughtRecordKey ===
                  thoughtRecord.thoughtRecordKey
                }
                setCurrentThoughtRecord={() => {
                  setCurrentThoughtRecord(thoughtRecord);
                }}
                shouldBeDone={!isCoach && thoughtRecord.shouldBeDone}
              />
            );
          })
        ) : (
          <>
            <Typography
              variant={"Caption"}
              sx={{
                width: "100%",
                textAlign: "center",
              }}
            >
              {selectedTab === "positive"
                ? isPositiveAvailable
                  ? "작성한 기록이 없어요."
                  : "아직 배우지 않았어요. 부정 기록에 집중해주세요."
                : "작성한 기록이 없어요."}
            </Typography>
          </>
        )}
        {/* <Box
          sx={{ height: "1500px", width: "100px", backgroundColor: "#000000" }}
        /> */}
      </List>
    </Stack>
  );
}